"use client"

import clsx from "clsx"
import React from "react"
import { AccordionContent, AccordionItem, Accordion as AccordionRoot, AccordionTrigger } from "../shadcn/accordion"
import { TIcon } from "./TIcon"

/**
 * @description 아코디언 개별 항목 데이터
 */
export interface TAccordionItem {
	/** 항목 고유 식별자 (미지정 시 index 사용) */
	id?: string
	/** 항목 헤더 (클릭 영역) */
	title: React.ReactNode
	/** 펼쳐질 때 표시되는 내용 */
	contents: React.ReactNode
}

/**
 * @description 접을 수 있는 아코디언. items 배열로 구성
 * @example
 * <TAccordion
 *   items={[
 *     { title: "섹션 1", contents: <p>내용 1</p> },
 *     { title: "섹션 2", contents: <p>내용 2</p> },
 *   ]}
 *   indicatorPosition="left"
 * />
 */
export interface TAccordionProps {
	/** 아코디언 항목 배열 */
	items: TAccordionItem[]
	/** 현재 펼쳐진 항목 인덱스 (제어 모드) */
	expandedIndex?: number
	/** 전체 비활성화 */
	disabled?: boolean
	/**
	 * 화살표 인디케이터 위치
	 * @default "left"
	 */
	indicatorPosition?: "left" | "right" | "flex"
	/** 추가 CSS 클래스 */
	className?: string
	/** 펼쳐진 항목 변경 콜백 */
	onChange?: (expandedIndex: number | undefined) => void
}

export const TAccordion = React.memo((props: TAccordionProps) => {
	const { items, expandedIndex: propExpandedIndex, disabled, indicatorPosition = "left", className, onChange } = props

	// expandedIndex prop이 전달되었을 때(undefined 포함) 제어 모드 — value를 undefined로 두어 닫힘 상태를
	// 제어할 수 있어야 하기 때문. 단순 `propExpandedIndex !== undefined` 판정 시 닫힘(controlled) → uncontrolled
	// 전환이 발생해 internal state(이전 값)가 부활하면서 "두 번 클릭해야 닫히는" 회귀가 발생함.
	const isControlled = "expandedIndex" in props
	const [internalExpandedIndex, setInternalExpandedIndex] = React.useState<number | undefined>(propExpandedIndex)

	const actualExpandedIndex = isControlled ? propExpandedIndex : internalExpandedIndex
	const accordionValue = actualExpandedIndex !== undefined ? actualExpandedIndex.toString() : ""

	const handleValueChange = React.useCallback(
		(value: string) => {
			const newExpandedIndex = value === "" ? undefined : parseInt(value, 10)

			if (!isControlled) {
				setInternalExpandedIndex(newExpandedIndex)
			}
			onChange?.(newExpandedIndex)
		},
		[isControlled, onChange]
	)

	if (!items || items.length === 0) {
		return null
	}

	return (
		<AccordionRoot type="single" collapsible value={accordionValue} disabled={disabled} onValueChange={handleValueChange} className={className}>
			{items.map((item, index) => (
				<AccordionItem key={item.id || `accordion-item-${index}`} value={index.toString()}>
					<AccordionTrigger>
						<div
							className={clsx(
								"trigger h-12 flex flex-1 items-center justify-between text-base font-bold ",
								{ "flex-row-reverse justify-end [&_.indicator]:mr-2": indicatorPosition === "left" },
								{ "justify-start [&_.indicator]:ml-2": indicatorPosition === "flex" }
							)}
						>
							<div>{item.title}</div>
							<TIcon size={16} name="dropdownDown" className="indicator fill-current pointer-events-none shrink-0 transition-transform" />
						</div>
					</AccordionTrigger>
					<AccordionContent>{item.contents}</AccordionContent>
				</AccordionItem>
			))}
		</AccordionRoot>
	)
})

TAccordion.displayName = "TAccordion"
